import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UserPrefsContext from '../../../context/UserPrefs/UserPrefsContext';
import { FormattedMessage } from 'react-intl';
import EVCard from '../../../components/EVCard/EVCard';
import sortEVs from './../../../functions/vehicle/Sort/sortEVs';
import filterEVs from '../../../functions/vehicle/Filter/filterEVs';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import getLocalVehicles from '../../../functions/vehicle/getLocalVehicles';
import './EVCatalog.scss';

const ITEMS_PER_PAGE = 24;

const EVCatalog = ({
  vehicles,
  hasIncentivesAndMatchScore,
  hasEvDetails,
  hasLinktoEv,
}) => {
  const userPrefs = useContext(UserPrefsContext);
  const [currentPage, setCurrentPage] = useState(1);

  const vehicleSortType = userPrefs.get('vehicleSortType');
  const vehicleFormFactorFilters = userPrefs.get('vehicleFormFactorFilters');
  const vehicleFuelTypeFilters = userPrefs.get('vehicleFuelTypeFilters');
  const vehicleMakeFilter = userPrefs.get('vehicleMakeFilter');
  const maxBudget = userPrefs.get('maxBudget');
  const locallyAvailableVehicleFilter = userPrefs.get('locallyAvailableVehicleFilter');

  useEffect(() => {
    userPrefs.set({ vehicleMakeFilter: 'All' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    vehicleSortType,
    vehicleFormFactorFilters,
    vehicleFuelTypeFilters,
    vehicleMakeFilter,
    maxBudget,
    locallyAvailableVehicleFilter,
  ]);

  if (!vehicles) return <LoadingSpinner />;
  const electricVehicles = userPrefs.get('locallyAvailableVehicleFilter')
    ? getLocalVehicles(vehicles)
    : vehicles;

  const sortedEvs = sortEVs(
    electricVehicles,
    userPrefs.get('vehicleSortType'),
    userPrefs.get('vehicleSortDirection'),
    userPrefs.get('milesDrivenDaily'),
    userPrefs.get('chargerLevel'),
    userPrefs.get('maxBudget'),
    userPrefs.get('minSeats'),
    userPrefs.get('salesTax'),
    userPrefs.get('downPayment')
  );

  const filteredAndSortedElectricVehicles = filterEVs(sortedEvs, {
    vehicleFormFactorFilters: userPrefs.get('vehicleFormFactorFilters'),
    vehicleFuelTypeFilters: userPrefs.get('vehicleFuelTypeFilters'),
    vehicleMakeFilter: userPrefs.get('vehicleMakeFilter'),
    vehicleMinSeatsFilter: userPrefs.get('minSeats'),
    vehicleRangeFilter: userPrefs.get('milesDrivenDaily'),
    vehicleBudgetFilter: userPrefs.get('maxBudget'),
    vehicleAvailableNowFilter: userPrefs.get('vehicleAvailableNowFilter'),
  });

  if (!filteredAndSortedElectricVehicles.length) {
    return (
      <div className="text-center" style={{ padding: '2rem' }}>
        <LoadingSpinner />
      </div>
    );
  }

  const totalPages = Math.ceil(
    filteredAndSortedElectricVehicles.length / ITEMS_PER_PAGE
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedVehicles = filteredAndSortedElectricVehicles.slice(
    startIndex,
    endIndex
  );

  return (
    <div>
      <div className="catalog-cards-container">
        {paginatedVehicles.map((ev) => (
          <EVCard
            ev={ev}
            hasEvDetails={hasEvDetails}
            hasIncentivesAndMatchScore={hasIncentivesAndMatchScore}
            hasLinktoEv={hasLinktoEv}
          />
        ))}
      </div>
      {totalPages > 1 && (
        <div
          className="pagination-controls"
          style={{ marginTop: '2rem', textAlign: 'center' }}
        >
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{ marginRight: '1rem', padding: '0.5rem 1rem' }}
          >
            <FormattedMessage id="previous" defaultMessage="Previous" />
          </button>
          <span style={{ margin: '0 1rem' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
            style={{ marginLeft: '1rem', padding: '0.5rem 1rem' }}
          >
            <FormattedMessage id="next" defaultMessage="Next" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EVCatalog;

EVCatalog.propTypes = {
  vehicles: PropTypes.array,
  hasEvDetails: PropTypes.bool,
  hasIncentivesAndMatchScore: PropTypes.bool,
  hasLinktoEv: PropTypes.bool,
};
